import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { useSpacePeriod } from '@/hooks/useSpacePeriod';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useSpaceScope } from '@/contexts/SpaceScopeContext';
import { useSpaceCommitments } from '@/hooks/queries/useSpaceCommitments';
import { useSpaceExpensesPage } from '@/hooks/queries/useSpaceExpensesPage';
import { hasCommitments, isCommitments, isPending } from '@/lib/spaces/commitments';
import { expenseFilter, type SpaceExpenseRow } from '@/lib/spaces/expenses';
import { isOverview } from '@/lib/spaces/home';
import { memberPhoto, participantName } from '@/lib/spaces/settlement';
import type { SpaceDetail } from '@/types/spaces';
import { SortChips, type SortChipOption } from '@/components/ui/SortChips';
import { SettlementAvatar } from './SettlementParts';
import { SpaceCommitmentsPanel } from './SpaceCommitmentsPanel';
import { SpaceExpenseDrawer } from './SpaceExpenseDrawer';
import { SpaceExpenseList } from './SpaceExpenseList';
import { SpacePageHeader } from './SpacePageHeader';

// `span` bounds the listing (every month the picker touches); `period` is the one month a ledger or a series reads.
type Session = Readonly<{ space: SpaceDetail; userId: string; period: string; span: string; filter: string; onFilter: (filter: string) => void }>;

export function SpaceExpensesPage() {
  const { space } = useSpaceScope();
  const { userId } = useAuth();
  const [params, setParams] = useSearchParams();
  // A filter this space cannot honour (a trip's `upcoming`, someone no longer on the roster, a typo) reads as
  // "Todas"; the address says so too instead of keeping a value the page ignores.
  const requested = params.get('filter');
  const honoured = space ? expenseFilter(space, requested) : null;
  useEffect(() => {
    if (honoured === null || requested === null || requested === honoured) return;
    const next = new URLSearchParams(params);
    next.delete('filter');
    setParams(next, { replace: true });
  }, [honoured, requested, params, setParams]);
  const { period, span } = useSpacePeriod(space?.kind ?? 'house');
  if (!space || !userId) return null;
  const filter = expenseFilter(space, params.get('filter'));
  const update = (name: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value); else next.delete(name);
    setParams(next);
  };
  const handleFilter = (value: string) => update('filter', value === 'all' ? null : value);
  return <section className="space-y-4 sm:space-y-5">
    <AppHeaderConfig hidePeriodPicker />
    <SpacePageHeader title="Despesas" space={space} period />
    <ExpensesBody key={`${userId}:${space.id}:${span}`} space={space} userId={userId} period={period} span={span} filter={filter} onFilter={handleFilter} />
  </section>;
}

// What people owe each other is not a period's: it is the home's Acerto, never a total on this page.
function ExpensesBody({ space, userId, period, span, filter, onFilter }: Session) {
  const [selected, setSelected] = useState<SpaceExpenseRow | null>(null);
  const automatic = useSpaceExpensesPage(space.id, span, 'auto', 0, 1);
  const commitments = useSpaceCommitments(space.id, hasCommitments(space.kind) ? period : null);
  const close = () => setSelected(null);
  // A failed revalidation keeps the previous body next to the error; neither chip may keep counting from it.
  const automaticLabel = automaticChip(filter, automatic.error ? undefined : automatic.data);
  const upcoming = upcomingChip(filter, commitments.error ? undefined : commitments.data);
  return <>
    <ExpenseFilters space={space} filter={filter} automaticLabel={automaticLabel} upcomingLabel={upcoming} onFilter={onFilter} />
    {filter === 'upcoming' ? <SpaceCommitmentsPanel space={space} period={period} commitments={commitments} />
      : <SpaceExpenseList key={filter} space={space} userId={userId} period={span} filter={filter} onOpen={setSelected} />}
    <OpenExpenseDrawer space={space} userId={userId} expense={selected} close={close} />
  </>;
}

// The same single-choice chips as Transações' "Ordenar"; a payer chip carries the person's thumbnail.
function ExpenseFilters({ space, filter, automaticLabel, upcomingLabel, onFilter }: Readonly<{ space: SpaceDetail; filter: string; automaticLabel: string | null; upcomingLabel: string | null; onFilter: (filter: string) => void }>) {
  const payerChip = (member: SpaceDetail['members'][number]): SortChipOption<string> => ({
    value: `payer:${member.userId}`, label: `${participantName(space, member.userId)} pagou`,
    leading: <SettlementAvatar name={participantName(space, member.userId)} photoUrl={memberPhoto(space, member.userId)} className="size-4 border-0 text-3xs" />,
  });
  const chips: SortChipOption<string>[] = [{ value: 'all', label: 'Todas' },
    ...(automaticLabel === null ? [] : [{ value: 'auto', label: automaticLabel }]),
    ...(upcomingLabel === null ? [] : [{ value: 'upcoming', label: upcomingLabel }]),
    ...space.members.map(payerChip)];
  return <SortChips options={chips} value={filter} onChange={onFilter} ariaLabel="Filtrar despesas" />;
}

// Each chip is an offer: nothing to count, or a failed or malformed read, simply withholds it. While it is the
// selected one it stays, without a count it does not have, and the list or the panel reports the failure itself.
function automaticChip(filter: string, data: unknown): string | null {
  if (!isOverview(data)) return filter === 'auto' ? 'Automáticas' : null;
  if (!data.expenses.total && filter !== 'auto') return null;
  return `Automáticas (${data.expenses.total.toLocaleString('pt-BR')})`;
}

function upcomingChip(filter: string, data: unknown): string | null {
  if (!isCommitments(data)) return filter === 'upcoming' ? 'A vencer' : null;
  if (!data.commitments.length && filter !== 'upcoming') return null;
  return `A vencer (${data.commitments.filter(isPending).length.toLocaleString('pt-BR')})`;
}

function OpenExpenseDrawer({ expense, ...props }: Readonly<{ space: SpaceDetail; userId: string; expense: SpaceExpenseRow | null; close: () => void }>) {
  if (!expense) return null;
  return <SpaceExpenseDrawer key={expense.id} expense={expense} model={props.space.model} {...props} />;
}
